import React, { useState } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Translate as TranslateIcon,
  SwapHoriz as SwapIcon,
  Save as SaveIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, addDoc, doc, setDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const Translate = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [inputText, setInputText] = useState('')
  const [translatedText, setTranslatedText] = useState('')
  const [loading, setLoading] = useState(false)
  const [isTurkishToEnglish, setIsTurkishToEnglish] = useState(true)

  const handleTranslate = async () => {
    if (!inputText.trim()) {
      toast.error('Lütfen çevrilecek metni girin')
      return
    }

    setLoading(true)

    try {
      // Önce basit kelime çevirisi dene
      const simpleTranslation = getFallbackTranslation(inputText, isTurkishToEnglish)
      
      // Eğer basit çeviri bulunduysa onu kullan
      if (simpleTranslation && !simpleTranslation.startsWith('[')) {
        setTranslatedText(simpleTranslation)
        toast.success('Çeviri tamamlandı!')
        setLoading(false)
        return
      }

      // Basit çeviri bulunamadıysa Google Translate API'yi kullan
      const sourceLang = isTurkishToEnglish ? 'tr' : 'en'
      const targetLang = isTurkishToEnglish ? 'en' : 'tr'
      
      // Android uygulamasındaki aynı Google Translate API
      const API_KEY = 'AIzaSyBLJUFQ5Hu31zJ3Rhzkgy4CwN1MeQiZP_w'
      const GOOGLE_TRANSLATE_URL = 'https://translation.googleapis.com/language/translate/v2'
      
      const response = await fetch(`${GOOGLE_TRANSLATE_URL}?key=${API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Englix-Education-App/1.0',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          q: inputText,
          source: sourceLang,
          target: targetLang,
          format: 'text'
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.data && data.data.translations && data.data.translations[0]) {
          const translatedText = data.data.translations[0].translatedText
          setTranslatedText(translatedText)
          toast.success('Çeviri tamamlandı!')
        } else {
          throw new Error('API çeviri sonucu geçersiz')
        }
      } else {
        const errorData = await response.json()
        throw new Error(`API hatası: ${response.status} - ${errorData.error?.message || 'Bilinmeyen hata'}`)
      }
    } catch (error) {
      console.error('Translation error:', error)
      
      // API çalışmazsa kullanıcıya bilgi ver
      setTranslatedText(`[Çeviri bulunamadı] ${inputText}`)
      toast.error('Çeviri yapılamadı. Lütfen farklı bir kelime deneyin.')
    } finally {
      setLoading(false)
    }
  }

  const getFallbackTranslation = (text, isTurkishToEnglish) => {
    // Genişletilmiş basit kelime çevirisi sözlüğü
    const translations = {
      // Temel kelimeler
      'merhaba': 'hello',
      'hello': 'merhaba',
      'selam': 'hi',
      'hi': 'selam',
      'günaydın': 'good morning',
      'good morning': 'günaydın',
      'iyi akşamlar': 'good evening',
      'good evening': 'iyi akşamlar',
      'iyi geceler': 'good night',
      'good night': 'iyi geceler',
      
      // Teşekkür ve ricalar
      'teşekkürler': 'thank you',
      'thank you': 'teşekkürler',
      'teşekkür ederim': 'thank you',
      'thanks': 'teşekkürler',
      'lütfen': 'please',
      'please': 'lütfen',
      'rica ederim': 'you\'re welcome',
      'you\'re welcome': 'rica ederim',
      
      // Evet/Hayır
      'evet': 'yes',
      'yes': 'evet',
      'hayır': 'no',
      'no': 'hayır',
      'tamam': 'okay',
      'okay': 'tamam',
      'ok': 'tamam',
      
      // Sıfatlar
      'güzel': 'beautiful',
      'beautiful': 'güzel',
      'iyi': 'good',
      'good': 'iyi',
      'kötü': 'bad',
      'bad': 'kötü',
      'büyük': 'big',
      'big': 'büyük',
      'küçük': 'small',
      'small': 'küçük',
      'yeni': 'new',
      'new': 'yeni',
      'eski': 'old',
      'old': 'eski',
      'genç': 'young',
      'young': 'genç',
      'yaşlı': 'old',
      'sıcak': 'hot',
      'hot': 'sıcak',
      'soğuk': 'cold',
      'cold': 'soğuk',
      
      // Renkler
      'kırmızı': 'red',
      'red': 'kırmızı',
      'mavi': 'blue',
      'blue': 'mavi',
      'yeşil': 'green',
      'green': 'yeşil',
      'sarı': 'yellow',
      'yellow': 'sarı',
      'beyaz': 'white',
      'white': 'beyaz',
      'siyah': 'black',
      'black': 'siyah',
      
      // Sayılar
      'bir': 'one',
      'one': 'bir',
      'iki': 'two',
      'two': 'iki',
      'üç': 'three',
      'three': 'üç',
      'dört': 'four',
      'four': 'dört',
      'beş': 'five',
      'five': 'beş',
      
      // Aile
      'anne': 'mother',
      'mother': 'anne',
      'baba': 'father',
      'father': 'baba',
      'kardeş': 'sibling',
      'sibling': 'kardeş',
      'arkadaş': 'friend',
      'friend': 'arkadaş',
      
      // Yiyecek/İçecek
      'su': 'water',
      'water': 'su',
      'ekmek': 'bread',
      'bread': 'ekmek',
      'çay': 'tea',
      'tea': 'çay',
      'kahve': 'coffee',
      'coffee': 'kahve',
      
      // Zaman
      'bugün': 'today',
      'today': 'bugün',
      'dün': 'yesterday',
      'yesterday': 'dün',
      'yarın': 'tomorrow',
      'tomorrow': 'yarın',
      'şimdi': 'now',
      'now': 'şimdi',
      
      // Yer
      'ev': 'home',
      'home': 'ev',
      'okul': 'school',
      'school': 'okul',
      'iş': 'work',
      'work': 'iş',
      'hastane': 'hospital',
      'hospital': 'hastane',
      
      // Eylemler
      'gitmek': 'to go',
      'to go': 'gitmek',
      'gelmek': 'to come',
      'to come': 'gelmek',
      'yemek': 'to eat',
      'to eat': 'yemek',
      'içmek': 'to drink',
      'to drink': 'içmek',
      'görmek': 'to see',
      'to see': 'görmek',
      'duymak': 'to hear',
      'to hear': 'duymak',
      'konuşmak': 'to speak',
      'to speak': 'konuşmak',
      'okumak': 'to read',
      'to read': 'okumak',
      'yazmak': 'to write',
      'to write': 'yazmak'
    }

    const lowerText = text.toLowerCase().trim()
    const translation = translations[lowerText]
    
    if (translation) {
      return translation
    }
    
    // Çeviri bulunamadı
    return null
  }

  const handleSaveToMemory = async () => {
    if (!inputText.trim() || !translatedText.trim()) {
      toast.error('Çeviri yapılmadan hafızaya kaydedilemez')
      return
    }

    try {
      // Android uygulamasındaki aynı yapıyı kullan
      const fromLang = isTurkishToEnglish ? 'tr' : 'en'
      const toLang = isTurkishToEnglish ? 'en' : 'tr'
      const documentId = `${inputText}_${fromLang}_${toLang}`
      
      const memoryData = {
        originalText: inputText,
        translatedText: translatedText,
        fromLanguage: fromLang,
        toLanguage: toLang,
        timestamp: Date.now()
      }

      console.log('Saving to memory with Android structure:', memoryData)
      console.log('Document ID:', documentId)
      console.log('Firebase path:', `users/${user.uid}/memory/${documentId}`)
      
      await setDoc(
        doc(db, 'users', user.uid, 'memory', documentId),
        memoryData
      )
      
      console.log('✅ Memory saved successfully to Firebase')
      toast.success('Hafızaya kaydedildi!')
    } catch (error) {
      console.error('❌ Error saving to memory:', error)
      toast.error('Hafızaya kaydedilirken hata oluştu')
    }
  }

  const handleCopyToClipboard = () => {
    if (translatedText) {
      navigator.clipboard.writeText(translatedText)
      toast.success('Panoya kopyalandı!')
    }
  }

  const handleSwapLanguages = () => {
    setIsTurkishToEnglish(!isTurkishToEnglish)
    setInputText(translatedText)
    setTranslatedText('')
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Hızlı Çeviri
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Language Direction Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight="bold" color="primary">
                Dil Yönü
              </Typography>
              <IconButton onClick={handleSwapLanguages} color="primary">
                <SwapIcon />
              </IconButton>
            </Box>
            
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Chip
                label={isTurkishToEnglish ? 'Türkçe' : 'İngilizce'}
                color="primary"
                variant="filled"
              />
              <Typography variant="h6" color="text.secondary">
                →
              </Typography>
              <Chip
                label={isTurkishToEnglish ? 'İngilizce' : 'Türkçe'}
                color="secondary"
                variant="filled"
              />
            </Box>
          </CardContent>
        </Card>

        {/* Input Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" mb={2}>
              Çevrilecek Metin
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              placeholder={isTurkishToEnglish ? 'Türkçe metin girin...' : 'Enter English text...'}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <TranslateIcon />}
              onClick={handleTranslate}
              disabled={!inputText.trim() || loading}
              sx={{
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #FF9800 30%, #FF5722 90%)'
              }}
            >
              {loading ? 'Çevriliyor...' : 'Çevir'}
            </Button>
          </CardContent>
        </Card>

        {/* Result Card */}
        {translatedText && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight="bold" color="secondary">
                  Çeviri Sonucu
                </Typography>
                <IconButton onClick={handleCopyToClipboard} color="primary" size="small">
                  <CopyIcon />
                </IconButton>
              </Box>
              
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  minHeight: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3
                }}
              >
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {translatedText}
                </Typography>
              </Box>
              
              {/* Hafızaya Kaydet Butonu */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<SaveIcon />}
                onClick={handleSaveToMemory}
                sx={{
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)',
                  boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #388E3C 30%, #4CAF50 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 5px 8px 2px rgba(76, 175, 80, .4)'
                  }
                }}
              >
                💾 Hafızaya Kaydet
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Alert */}
        <Alert severity="info">
          Çevirileri hafızaya kaydedebilir ve daha sonra "Hafıza" sayfasından görüntüleyebilirsiniz.
        </Alert>
      </Container>
      </Box>
    </SpaceTheme>
  )
}

export default Translate
